import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from "react";

// context
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { canShowCutleryOptInWidget } from "@cart-client/helpers/Cart";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

// analytics
import { clickCutleryOptInWidget, impressionCutleryOptInWidget } from "@cart-client/analytics/events";

//utils
import { SessionVisibility, useLocalStorageBasedFrequencyCapping } from "@swiggy-private/checkout-core";
import { getNestedValue } from "@swiggy-private/js-utils";
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";

//types
import { BOOLEAN_AS_STRING } from "@swiggy-private/cart-types";

const CUTLERY_FTUE_FREQUENCY = "cutlery_ftue_frequency";

interface ICutleryOptInOutHook {
    showCutleryTile: boolean;
    isCutleryOpted: boolean;
    toggleCutlery: VoidFunction;
    handleNudgeClosed: Dispatch<SetStateAction<boolean>>;
    nudgeClosed: boolean;
    isFtueForCutlery?: boolean;
}

export function useCutleryOptInOut(): ICutleryOptInOutHook {
    const [nudgeClosed, handleNudgeClosed] = useState<boolean>(false);
    const [isFtueForCutlery, setIsFtueForCutlery] = useState<boolean>(false);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { cartState } = useContext(CartContext);
    const { doUpdateCart } = useContext(UpdateCartContext);
    const { data } = useContext(CartFlowContext);
    const { reviewedCart: cartResponse, localCart } = cartState;

    const { shouldProceed } = useLocalStorageBasedFrequencyCapping({
        key: CUTLERY_FTUE_FREQUENCY,
        maxFrequency: 2,
        sessionVisibility: SessionVisibility.ONCE_PER_SESSION,
    });

    const isCutleryOpted = useMemo(() => {
        return getNestedValue(cartResponse, "data.cart_saved_preferences.is_cutlery_opted") === BOOLEAN_AS_STRING.TRUE;
    }, [cartResponse]);

    const showCutleryTile = canShowCutleryOptInWidget(cartResponse?.data);

    const toggleCutlery = useCallback(() => {
        clickCutleryOptInWidget(clickEvent, !isCutleryOpted ? 1 : 0);
        doUpdateCart({
            ...localCart,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            cart_saved_preferences: {
                ...localCart?.cart_saved_preferences,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                is_cutlery_opted: isCutleryOpted ? "false" : "true",
            },
        });
    }, [clickEvent, doUpdateCart, isCutleryOpted, localCart]);

    useEffect(() => {
        if (showCutleryTile) {
            impressionCutleryOptInWidget(impressionEvent, isCutleryOpted ? 1 : 0);
        }
    }, [impressionEvent, isCutleryOpted, showCutleryTile, shouldProceed]);

    useEffect(() => {
        if (!isGiftingCartSource(data?.cartSource)) {
            setIsFtueForCutlery(shouldProceed());
        }
    }, [data?.cartSource, shouldProceed]);

    return {
        isCutleryOpted,
        toggleCutlery,
        showCutleryTile,
        isFtueForCutlery: showCutleryTile && isFtueForCutlery,
        handleNudgeClosed,
        nudgeClosed,
    };
}
